// Pure formatting for the experimentation surfaces: the injection 2x2 strip on a
// prompt-section card and the /experiments page. Kept out of the components so
// the "what does a null render as" decisions are unit-tested rather than eyeballed.
//
// One rule runs through all of it: an UNMEASURED value prints "-", never 0. Null
// is unknown; 0 is a real answer; the two must never render the same (the same
// promise ab-tests.tsx makes about its rates).
import { csvCell } from "@recruiter/shared";
import type {
  AdminArmStrip,
  AdminExperimentDecision,
  AdminExperimentRecord,
  AdminExperimentRegister,
  AdminExperimentationResponse,
  AdminInjectionCells,
  AdminInjectionDials,
  AdminLastChange,
} from "@recruiter/shared";

export const INJECTION_WINDOWS = ["7d", "30d"] as const;
export type InjectionWindow = (typeof INJECTION_WINDOWS)[number];

/** The dial defaults an injection is born with: nobody enrolled, sent whenever
 *  the trigger fires, never sent otherwise. Mirrors the backend's code default. */
export const DEFAULT_INJECTION_DIALS = { exposure: 0, pTriggered: 100, pUntriggered: 0 } as const;

export type InjectionDialDraft = {
  exposure: number;
  pTriggered: number;
  pUntriggered: number;
};

export function dialDraftFrom(dials: AdminInjectionDials | null | undefined): InjectionDialDraft {
  if (!dials) return { ...DEFAULT_INJECTION_DIALS };
  return {
    exposure: dials.exposure,
    pTriggered: dials.pTriggered,
    pUntriggered: dials.pUntriggered,
  };
}

export function dialsMoved(draft: InjectionDialDraft, base: InjectionDialDraft): boolean {
  return (
    draft.exposure !== base.exposure ||
    draft.pTriggered !== base.pTriggered ||
    draft.pUntriggered !== base.pUntriggered
  );
}

/** The sentence under the pending row: what will change, from what to what. */
export function describeDialMove(draft: InjectionDialDraft, base: InjectionDialDraft): string {
  const parts: string[] = [];
  if (draft.exposure !== base.exposure) {
    parts.push(`exposure ${base.exposure}% to ${draft.exposure}%`);
  }
  if (draft.pTriggered !== base.pTriggered) {
    parts.push(`send when triggered ${base.pTriggered}% to ${draft.pTriggered}%`);
  }
  if (draft.pUntriggered !== base.pUntriggered) {
    parts.push(`send even if not triggered ${base.pUntriggered}% to ${draft.pUntriggered}%`);
  }
  return parts.length ? `Move ${parts.join(", ")}?` : "";
}

/** Every arm's share INCLUDING the baseline's, in declaration order.
 *
 *  Read straight off `record.armAllocation`, which the backend computes once in
 *  `_arm_allocation`. This used to be re-derived here from the RAW stored
 *  allocation, with a remainder computed only for an arm literally called
 *  `default`, so every other baseline (product, logic, config, section) printed
 *  0% and the always-on A/A read `a 0%, b 50%`. The rule now lives in one place,
 *  server side, and this renders what it says. */
export function armShares(row: ArmSharesSource): { arm: string; pct: number; baseline: boolean }[] {
  const shares = row.armAllocation ?? {};
  const arms = row.arms.length > 0 ? row.arms : Object.keys(shares);
  return arms
    .filter((arm) => arm in shares)
    .map((arm, i) => ({ arm, pct: shares[arm] ?? 0, baseline: i === 0 }));
}

/** Does this record actually divide its population?
 *
 *  True when at least TWO arms hold somebody. A dial parked at either end fails
 *  it: everybody is in one arm, so a filter naming this experiment selects the
 *  whole board (that arm) or an empty one (any other), and an empty board reads
 *  as a collapse in conversion rather than as a filter that could not narrow.
 *
 *  It is asked of the SHARES rather than of a dial percentage on purpose. A
 *  section's dial counts the share who GET the section, so 100% is the shipped
 *  state and not a full ramp; a rule written as `pct > 0 && pct < 100` over
 *  `dialledPct` would hide every shipped section and keep every parked one. The
 *  shares come from the server's `_arm_allocation`, which already knows each
 *  kind's baseline, so one test is right for all six. */
export function splitsPopulation(row: ArmSharesSource): boolean {
  return armShares(row).filter((s) => s.pct > 0).length > 1;
}

/** The two fields `armShares` needs, so a projected allocation can borrow it. */
export type ArmSharesSource = {
  arms: readonly string[];
  armAllocation: Record<string, number>;
};

/** `aa_control` -> "a 50% (baseline), b 50%". The baseline is LABELLED rather
 *  than merely listed, so a reader can tell a share that was set from one that
 *  is the remainder of everything else. "-" when the record declares no arms. */
export function summariseAllocation(row: ArmSharesSource): string {
  const parts = armShares(row).map((s) => `${s.arm} ${s.pct}%${s.baseline ? " (baseline)" : ""}`);
  return parts.length === 0 ? "-" : parts.join(", ");
}

export function formatCalls(n: number | null | undefined): string {
  return n === null || n === undefined ? "-" : n.toLocaleString();
}

/** Cost per call in USD, four decimals: a call is fractions of a cent and two
 *  decimals would round every arm to $0.00. */
export function formatCostPerCall(usd: number | null | undefined): string {
  return usd === null || usd === undefined ? "-" : `$${usd.toFixed(4)}`;
}

/** A 0..1 rate as a percentage with one decimal. */
export function formatRate(rate: number | null | undefined): string {
  return rate === null || rate === undefined ? "-" : `${(rate * 100).toFixed(1)}%`;
}

export function formatLatencyMs(ms: number | null | undefined): string {
  return ms === null || ms === undefined ? "-" : `${Math.round(ms).toLocaleString()} ms`;
}

export type StripCells = { arm: string; calls: string; cost: string; errors: string; p95: string };

export function formatStrip(strip: AdminArmStrip): StripCells {
  return {
    arm: strip.arm,
    calls: formatCalls(strip.calls),
    cost: formatCostPerCall(strip.costPerCallUsd),
    errors: formatRate(strip.errorRate),
    p95: formatLatencyMs(strip.p95LatencyMs),
  };
}

export type InjectionCellStrings = {
  turns: string;
  control: string;
  triggeredSent: string;
  triggeredWithheld: string;
  untriggeredSent: string;
  untriggeredWithheld: string;
  triggerRate: string;
};

const EMPTY_CELLS: InjectionCellStrings = {
  turns: "-",
  control: "-",
  triggeredSent: "-",
  triggeredWithheld: "-",
  untriggeredSent: "-",
  untriggeredWithheld: "-",
  triggerRate: "-",
};

/** The 2x2 plus control for ONE injection over ONE window, as strings. Every
 *  cell is "-" while the snapshot is still computing or the key has no row, so
 *  "not counted yet" never reads as "counted zero". */
export function formatInjectionCells(
  snapshot: Pick<AdminExperimentationResponse, "state" | "injectionCells"> | undefined,
  key: string,
  window: InjectionWindow,
): InjectionCellStrings {
  if (!snapshot || snapshot.state !== "ready") return EMPTY_CELLS;
  const cells: AdminInjectionCells | undefined = snapshot.injectionCells[key]?.[window];
  if (!cells) return EMPTY_CELLS;
  return {
    turns: formatCalls(cells.turns),
    control: formatCalls(cells.control),
    triggeredSent: formatCalls(cells.triggeredSent),
    triggeredWithheld: formatCalls(cells.triggeredWithheld),
    untriggeredSent: formatCalls(cells.untriggeredSent),
    untriggeredWithheld: formatCalls(cells.untriggeredWithheld),
    triggerRate: formatRate(cells.triggerRate),
  };
}

// ---------------------------------------------------------------------------
// The per-kind allocation control on /experiments/$key.
//
// One draft shape for every kind, `{name: pct}`, because that is what
// `POST /api/admin/experimentation/{key}/allocation` takes; what the names ARE
// depends on the kind. A product experiment names its non-baseline arms, a
// prompt slot its non-default approaches (both: the remainder is the baseline's,
// so the named ones sum to at most 100), and an injection its three dials, which
// are independent probabilities and do not sum to anything.

export type AllocationDraft = Record<string, number>;

/** The wire keys of an injection's three dials, in slider order. Mirrors
 *  `InjectionDials.as_dict()` on the backend; the registry record's
 *  `allocation` carries the same keys. */
export const INJECTION_DIAL_KEYS = ["exposure", "p_triggered", "p_untriggered"] as const;

export const INJECTION_DIAL_LABELS: Record<(typeof INJECTION_DIAL_KEYS)[number], string> = {
  exposure: "Exposure",
  p_triggered: "Send when triggered",
  p_untriggered: "Send even if not triggered",
};

/** The payload key a section's dial is written under. The RECORD's allocation
 *  names the `absent` arm's share instead (`registry.section_dial_records`), so
 *  the two shapes are converted at exactly one place, `allocationDraftFrom`. */
export const SECTION_DIAL_KEY = "dial";

/** Every registry kind this console can allocate, which is now all six. `logic`
 *  and `config` were missing because the ROUTE could not resolve them; that is
 *  fixed, and a console that can start an experiment it cannot stop is worse
 *  than one that offers neither. */
export const ALLOCATABLE_KINDS = [
  "ui",
  "section",
  "prompt",
  "injection",
  "logic",
  "config",
] as const;
export type AllocatableKind = (typeof ALLOCATABLE_KINDS)[number];

export function allocatableKind(kind: string): AllocatableKind | null {
  return (ALLOCATABLE_KINDS as readonly string[]).includes(kind) ? (kind as AllocatableKind) : null;
}

/** The key an allocation control reads LIVE under, or "" to read nothing and
 *  sit on the snapshot's copy instead.
 *
 *  The board mounts one control per experiment, so "always read live" is not a
 *  per-control decision, it is a fan-out: on 2026-09-16 opening the board fired
 *  95 `GET .../allocation` requests in under three seconds, 19 of which Cloud
 *  Run refused outright ("no available instance") because the admin service
 *  holds max_instances x concurrency request slots and that was 40 at the time.
 *  Nothing was slow and nothing was heavy - the median of those requests was
 *  72ms - there were simply ninety-five of them, for numbers the snapshot the
 *  board already fetched carries on every row.
 *
 *  Two things earn the request, and the second is the safety one:
 *
 *  `focused` - an operator is looking at this control: the detail page, the
 *  expanded register row, an OPEN board card. A control nobody has opened shows
 *  `record.allocation`, which is what the sentence beside its slider
 *  (`summariseAllocation`) has always shown, so the card gets more internally
 *  consistent rather than less. The cost is a dial as old as the snapshot (a
 *  worker tick, so up to half an hour).
 *
 *  `touched` - somebody has REACHED FOR a dial (pointer down or focus), or has
 *  already moved one. This one is not about freshness, it is about not writing
 *  the wrong number: a move POSTs an ABSOLUTE allocation, not a delta, so a
 *  payload built on a snapshot-aged base could lower a dial the operator meant
 *  to raise, or revert a colleague's move to a dial they never touched. The
 *  board's dial is DESIGNED to be usable without opening the card
 *  (`experiment-board.tsx`: the name opens the panel, not the card, precisely
 *  so reaching for the slider does not), so "opened" alone would not cover the
 *  write path.
 *
 *  It is the POINTER rather than the first value change on purpose: that puts
 *  the read in flight while the operator is still dragging, so by the time
 *  there is a move to describe it is described against the server's real
 *  current value. Two things then make the write safe rather than merely
 *  well-timed, and they live in `experiment-allocation.tsx`: the draft is an
 *  OVERLAY of only the dials that moved (so untouched ones read through to the
 *  live base rather than being pinned to the tick's), and Apply waits for
 *  `isSuccess` (so nothing is posted against a base that never arrived). */
export function liveAllocationKey(
  key: string,
  kind: AllocatableKind | null,
  read: { focused: boolean; touched: boolean },
): string {
  if (kind === null) return "";
  return read.focused || read.touched ? key : "";
}

/** What an allocation control SHOWS, and what Apply POSTs: the server's current
 *  values with this operator's moves laid over them.
 *
 *  `moves` is an overlay of ONLY the dials that were moved, never a whole
 *  allocation, and that is the difference between a correct write and a silent
 *  revert. A move POSTs an ABSOLUTE allocation. If the draft carried every dial,
 *  it would carry the untouched ones at whatever value they held when the drag
 *  began - the worker's tick, on a board card that had not read live yet - and
 *  applying it would put a colleague's move to a DIFFERENT arm of the same
 *  experiment back to where it was before they made it. Nobody would see it
 *  happen: the operator moved one slider and the payload quietly re-set the rest.
 *
 *  As an overlay, an untouched dial reads through to whatever `base` is now. So
 *  the live allocation landing mid-drag simply appears, and so does a concurrent
 *  move to another arm; the pending row describes the real delta, and what Apply
 *  sends preserves it. */
export function overlayAllocation(
  base: AllocationDraft,
  moves: AllocationDraft | null,
): AllocationDraft {
  return moves === null ? base : { ...base, ...moves };
}

/** Which names the draft carries for this record: the three injection dials, a
 *  section's single dial, or the non-baseline arms (a record's arms are baseline
 *  FIRST) for the other four kinds. */
export function allocationNames(kind: AllocatableKind, arms: readonly string[]): string[] {
  if (kind === "injection") return [...INJECTION_DIAL_KEYS];
  if (kind === "section") return [SECTION_DIAL_KEY];
  return arms.slice(1);
}

/** The draft the sliders start on: what the record says, with an unallocated
 *  name at 0 (never dropped, so the payload names every arm the route expects).
 *  An injection's defaults mirror `DEFAULT_INJECTION_DIALS`.
 *
 *  A SECTION is the one conversion: the record carries `{absent: 100 - dial}`
 *  and the route takes `{dial: pct}`, so the dial is recovered here rather than
 *  at every call site. A slot with no stored dial ships to everybody, which is
 *  dial 100 and not dial 0. */
export function allocationDraftFrom(
  kind: AllocatableKind,
  arms: readonly string[],
  allocation: Record<string, number>,
): AllocationDraft {
  if (kind === "section") {
    const stored = allocation[SECTION_DIAL_KEY];
    if (typeof stored === "number") return { [SECTION_DIAL_KEY]: stored };
    return { [SECTION_DIAL_KEY]: Math.max(0, 100 - (allocation.absent ?? 0)) };
  }
  const defaults: Record<string, number> =
    kind === "injection"
      ? {
          exposure: DEFAULT_INJECTION_DIALS.exposure,
          p_triggered: DEFAULT_INJECTION_DIALS.pTriggered,
          p_untriggered: DEFAULT_INJECTION_DIALS.pUntriggered,
        }
      : {};
  return Object.fromEntries(
    allocationNames(kind, arms).map((name) => [name, allocation[name] ?? defaults[name] ?? 0]),
  );
}

/** What the arm shares WOULD be if this draft were applied: the same arithmetic
 *  the backend's `_arm_allocation` does, over an unapplied draft rather than a
 *  stored allocation.
 *
 *  This is the whole "show me what the move does before I make it" affordance.
 *  It is derived from the draft alone, with no request: a projection that had to
 *  ask the server would either be slow enough to stop being a preview or would
 *  need a request-cycle aggregate, and neither belongs behind a slider. */
export function projectedShares(
  kind: AllocatableKind,
  arms: readonly string[],
  draft: AllocationDraft,
): Record<string, number> {
  if (arms.length === 0) return {};
  const baseline = arms[0]!;
  if (kind === "injection") {
    const exposure = draft.exposure ?? 0;
    const out: Record<string, number> = { [baseline]: Math.max(0, 100 - exposure) };
    for (const arm of arms.slice(1)) out[arm] = exposure;
    return out;
  }
  if (kind === "section") {
    const dial = draft[SECTION_DIAL_KEY] ?? 100;
    const out: Record<string, number> = { [baseline]: dial };
    for (const arm of arms.slice(1)) out[arm] = Math.max(0, 100 - dial);
    return out;
  }
  const named = arms.slice(1).map((arm) => [arm, draft[arm] ?? 0] as const);
  const total = named.reduce((n, [, v]) => n + v, 0);
  return { [baseline]: Math.max(0, 100 - total), ...Object.fromEntries(named) };
}

export function allocationMoved(draft: AllocationDraft, base: AllocationDraft): boolean {
  const names = new Set([...Object.keys(draft), ...Object.keys(base)]);
  for (const name of names) if ((draft[name] ?? 0) !== (base[name] ?? 0)) return true;
  return false;
}

/** The share of the population OUTSIDE the baseline: the sum of the named arms
 *  for a product experiment or a slot, the exposure for an injection. The
 *  number the lifecycle state (off / canary / running / full) is derived from. */
export function headlinePct(kind: AllocatableKind, draft: AllocationDraft): number {
  if (kind === "injection") return draft.exposure ?? 0;
  if (kind === "section") return draft[SECTION_DIAL_KEY] ?? 0;
  return Object.values(draft).reduce((n, v) => n + v, 0);
}

/** The sentence that refuses a draft before the route does, or null when it is
 *  fine. Every value is a whole 0..100; arms and approaches sum to at most 100
 *  because the baseline takes the remainder. */
export function validateAllocation(kind: AllocatableKind, draft: AllocationDraft): string | null {
  for (const [name, value] of Object.entries(draft)) {
    if (!Number.isInteger(value) || value < 0 || value > 100) {
      return `${name} must be a whole percentage between 0 and 100.`;
    }
  }
  if (kind === "injection") {
    const missing = INJECTION_DIAL_KEYS.filter((k) => !(k in draft));
    if (missing.length) return `An injection needs all three dials; missing ${missing.join(", ")}.`;
    return null;
  }
  if (kind === "section") {
    return SECTION_DIAL_KEY in draft
      ? null
      : `A section payload is exactly {"${SECTION_DIAL_KEY}": pct}.`;
  }
  if (Object.keys(draft).length === 0) return "Name at least one arm.";
  const total = headlinePct(kind, draft);
  if (total > 100) {
    return `Allocated ${total}%. The rest belongs to the baseline, so the other arms cannot exceed 100.`;
  }
  return null;
}

/** The sentence under the pending row: what will change, from what to what. */
export function describeAllocationMove(
  kind: AllocatableKind,
  draft: AllocationDraft,
  base: AllocationDraft,
): string {
  const label = (name: string): string => {
    if (kind === "injection") {
      return (
        INJECTION_DIAL_LABELS[name as (typeof INJECTION_DIAL_KEYS)[number]] ?? name
      ).toLowerCase();
    }
    return kind === "section" && name === SECTION_DIAL_KEY ? "share who get the section" : name;
  };
  const parts: string[] = [];
  for (const name of Object.keys(draft)) {
    const from = base[name] ?? 0;
    const to = draft[name] ?? 0;
    if (from !== to) parts.push(`${label(name)} ${from}% to ${to}%`);
  }
  return parts.length ? `Move ${parts.join(", ")}?` : "";
}

/** How much of the population sits outside the baseline, from a record's
 *  allocation: what the A/B filter and the list call "dialled". */
export function dialledPct(kind: string, allocation: Record<string, number>): number {
  const k = allocatableKind(kind);
  if (k === "injection") return allocation.exposure ?? 0;
  return Object.entries(allocation)
    .filter(([name]) => name !== "default" && name !== "control")
    .reduce((n, [, v]) => n + v, 0);
}

/** The tabs of `/experiments/$key`, in strip order. `cells` and `triggers` are
 *  offered only for an injection; the page decides that from the record. */
export const EXPERIMENT_TABS = [
  "outcomes",
  "strip",
  "cells",
  "guardrails",
  "timeline",
  "triggers",
  "issues",
] as const;
export type ExperimentTab = (typeof EXPERIMENT_TABS)[number];

export const EXPERIMENT_TAB_LABELS: Record<ExperimentTab, string> = {
  outcomes: "Outcomes",
  strip: "Strip",
  cells: "Cells",
  guardrails: "Guardrails",
  timeline: "Timeline",
  triggers: "Triggers",
  issues: "Issues",
};

/** The tabs a record of this kind offers. */
export function tabsFor(kind: string): readonly ExperimentTab[] {
  return kind === "injection"
    ? EXPERIMENT_TABS
    : EXPERIMENT_TABS.filter((t) => t !== "cells" && t !== "triggers");
}

/** A tab from a URL param, or undefined for the page's default. Unrecognised
 *  reads as absent rather than throwing: a stale link should open the page. */
export function parseExperimentTab(raw: unknown): ExperimentTab | undefined {
  return typeof raw === "string" && (EXPERIMENT_TABS as readonly string[]).includes(raw)
    ? (raw as ExperimentTab)
    : undefined;
}

/** A parity count is a DISAGREEMENT count (assigned vs served, and the like),
 *  so zero is the healthy reading and anything else needs a red eye on it. */
export function parityTone(count: number): "success" | "danger" {
  return count === 0 ? "success" : "danger";
}

/** `from -> to`, with a missing `from` printed as the birth of the entity. */
export function formatTransition(from: string | null | undefined, to: string): string {
  return `${from ?? "(new)"} -> ${to}`;
}

/** A pasted project id, trimmed and lower-cased; null when it is not a uuid, so
 *  the lookup box refuses before the click rather than 404ing after it. */
export function parseProjectId(raw: string): string | null {
  const v = raw.trim().toLowerCase();
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(v) ? v : null;
}

// ───────────────────────────────────────────────────────────────────────────
// The register: filters, search, sort, saved views, URL round trip, CSV
// ───────────────────────────────────────────────────────────────────────────
//
// All of it is pure over the wire records, for one reason: the register is a
// board somebody makes a decision from, and every one of these rules is a claim
// about what they will and will not see. A filter that silently drops a running
// experiment, or a sort that puts a breached arm on page two, is a bug you
// cannot find by looking at the screen. So the rules live here, under test, and
// the page only renders what they return.
//
// The second rule throughout: the backend says "unknown" rather than "zero" for
// anything it could not measure, and so does every function here. `registerFacts`
// is absent when the facts read failed; a row like that sorts LAST rather than
// as an idle one, because "we do not know" is not "nothing is happening".

/** The register's derived facts, or null when the backend could not read them. */
export function registerFactsOf(row: AdminExperimentRecord): AdminExperimentRegister | null {
  return row.registerFacts ?? null;
}

/** Every state a register row can print. The seven the backend derives
 *  (`web/schemas_experimentation.py::RegisterState`) plus the one only this side
 *  knows about: `unknown`, for a row whose `registerFacts` could not be read.
 *  That is not "quiet", it is "we could not tell", and the two must not share a
 *  word the way `shipped` and `off` used to share `idle`. */
export const REGISTER_STATES = [
  "split",
  "canary",
  "decided",
  "shipped",
  "promoted",
  "killed",
  "off",
  "unknown",
] as const;
export type RegisterStateName = (typeof REGISTER_STATES)[number];

/** The states that mean population is actually being SPLIT right now. These are
 *  the rows pinned to the top of the register, because "what is live" is still
 *  the first question even when the answer to "what else is there" is ninety-six
 *  other rows. */
export const LIVE_STATES: readonly RegisterStateName[] = ["split", "canary"];

export function isLiveState(state: string): boolean {
  return (LIVE_STATES as readonly string[]).includes(state);
}

/** The lifecycle state, or "unknown" when the facts are missing. Never a state
 *  that claims to know: an experiment we could not read is not one we know is
 *  quiet. */
export function stateOf(row: AdminExperimentRecord): RegisterStateName {
  const state = registerFactsOf(row)?.state;
  return state && (REGISTER_STATES as readonly string[]).includes(state)
    ? (state as RegisterStateName)
    : "unknown";
}

/** `{state: count}` for a set of rows, in `REGISTER_STATES` order and with the
 *  zeroes dropped: a group header says "12 shipped, 2 split", never "0 killed". */
export function stateBreakdown(
  rows: readonly AdminExperimentRecord[],
): { state: RegisterStateName; count: number }[] {
  const counts = new Map<RegisterStateName, number>();
  for (const row of rows) {
    const state = stateOf(row);
    counts.set(state, (counts.get(state) ?? 0) + 1);
  }
  return REGISTER_STATES.filter((s) => (counts.get(s) ?? 0) > 0).map((state) => ({
    state,
    count: counts.get(state) ?? 0,
  }));
}

export function daysRunningOf(row: AdminExperimentRecord): number | null {
  return registerFactsOf(row)?.daysRunning ?? null;
}

export function lastChangeOf(row: AdminExperimentRecord): AdminLastChange | null {
  return registerFactsOf(row)?.lastChange ?? null;
}

export function decisionOf(row: AdminExperimentRecord): AdminExperimentDecision | null {
  return registerFactsOf(row)?.decision ?? null;
}

/** Total units across the arms, or null when the tick does not carry them.
 *  Null and 0 are different answers and the column prints them differently. */
export function totalExposuresOf(row: AdminExperimentRecord): number | null {
  const exposures = registerFactsOf(row)?.exposures;
  if (!exposures) return null;
  const values = Object.values(exposures);
  return values.length === 0 ? null : values.reduce((n, v) => n + v, 0);
}

/** The agent a key belongs to, from its prefix: `mira.tone` -> "mira".
 *  Keys with no dot (the product cohorts) belong to no single agent and answer
 *  "" so the agent filter simply does not match them, rather than inventing a
 *  bucket called "aa_control". */
export function agentOfKey(key: string): string {
  const dot = key.indexOf(".");
  return dot > 0 ? key.slice(0, dot) : "";
}

/** Every agent present in a snapshot, for the filter's own dropdown. */
export function agentsIn(rows: readonly AdminExperimentRecord[]): string[] {
  const found = new Set<string>();
  for (const row of rows) {
    const agent = agentOfKey(row.key);
    if (agent) found.add(agent);
  }
  return [...found].sort();
}

export type RegisterFilters = {
  kind: string;
  state: string;
  agent: string;
  unit: string;
  owner: string;
  layer: string;
  /** OFF by default, and that is the headline fix. It used to be on, which meant
   *  the register hid every experiment that was not mid-split: on a system where
   *  almost nothing is dialled, ninety-six rows out of ninety-seven. It survives
   *  as a narrowing chip like every other filter, never as the default. */
  doingSomething: boolean;
  hasBreaches: boolean;
  hasMonday: boolean;
  /** Only rows whose last lifecycle row is within this many days. */
  changedWithinDays: number | null;
  q: string;
};

export const DEFAULT_REGISTER_FILTERS: RegisterFilters = {
  kind: "",
  state: "",
  agent: "",
  unit: "",
  owner: "",
  layer: "",
  doingSomething: false,
  hasBreaches: false,
  hasMonday: false,
  changedWithinDays: null,
  q: "",
};

/** What a row is judged against: which keys are in breach right now, what time
 *  it is (for "changed in the last N days"), and who is asking (for "mine"). */
export type RegisterContext = {
  breachedKeys: ReadonlySet<string>;
  now: number;
  me: string;
};

/** The keys with at least one guardrail breach on this tick. */
export function breachedKeys(data: AdminExperimentationResponse): Set<string> {
  return new Set(data.breaches.map((b) => b.experiment));
}

/** Every string a search box may match against, folded to lower case once.
 *  Key, label, description, arm names, Monday id, actor, KIND and UNIT: the
 *  things somebody types when they are looking for an experiment they half
 *  remember, and "injection" or "project" are two of them. */
export function registerHaystack(row: AdminExperimentRecord): string {
  const facts = registerFactsOf(row);
  return [
    row.key,
    row.label,
    row.description,
    row.owner,
    row.monday,
    row.layer,
    row.kind,
    row.unit,
    row.arms.join(" "),
    facts?.state ?? "",
    facts?.lastChange?.actorEmail ?? "",
    facts?.decision?.decidedBy ?? "",
  ]
    .join(" ")
    .toLowerCase();
}

/** One piece of a cell, split on the search terms so the page can mark the part
 *  that matched. Returned as data rather than as markup so the rule is unit
 *  tested and the component only decides what a hit LOOKS like.
 *
 *  Terms are matched independently and longest first, so searching "mira tone"
 *  highlights both words wherever they land rather than only the literal phrase,
 *  which is how the filter itself narrows. */
export function highlightParts(text: string, q: string): { text: string; hit: boolean }[] {
  const terms = q
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .sort((a, b) => b.length - a.length);
  if (terms.length === 0 || text === "") return [{ text, hit: false }];
  const lower = text.toLowerCase();
  const marked = new Array<boolean>(text.length).fill(false);
  for (const term of terms) {
    let from = lower.indexOf(term);
    while (from !== -1) {
      for (let i = from; i < from + term.length; i += 1) marked[i] = true;
      from = lower.indexOf(term, from + term.length);
    }
  }
  const out: { text: string; hit: boolean }[] = [];
  let start = 0;
  for (let i = 1; i <= text.length; i += 1) {
    if (i === text.length || marked[i] !== marked[start]) {
      out.push({ text: text.slice(start, i), hit: marked[start] === true });
      start = i;
    }
  }
  return out;
}

export function matchesRegisterSearch(row: AdminExperimentRecord, q: string): boolean {
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  const haystack = registerHaystack(row);
  // Every whitespace-separated term must appear, so "mira tone" narrows rather
  // than widening. Substring, not prefix: half a key is the common case.
  return needle.split(/\s+/).every((term) => haystack.includes(term));
}

/** Days since an ISO timestamp, or null when it is absent or unparseable. */
export function daysSince(iso: string | null | undefined, now: number): number | null {
  if (!iso) return null;
  const at = Date.parse(iso);
  if (Number.isNaN(at)) return null;
  return (now - at) / 86_400_000;
}

export function matchesRegisterFilters(
  row: AdminExperimentRecord,
  filters: RegisterFilters,
  ctx: RegisterContext,
): boolean {
  const state = stateOf(row);
  if (filters.kind && row.kind !== filters.kind) return false;
  if (filters.unit && row.unit !== filters.unit) return false;
  if (filters.state && state !== filters.state) return false;
  if (filters.agent && agentOfKey(row.key) !== filters.agent) return false;
  if (filters.layer && row.layer !== filters.layer) return false;
  if (filters.owner && row.owner.toLowerCase() !== filters.owner.toLowerCase()) return false;
  if (filters.hasMonday && !row.monday) return false;
  if (filters.hasBreaches && !ctx.breachedKeys.has(row.key)) return false;
  // "Doing something" now means SPLITTING somebody. `shipped` is a real state
  // and so is `off`, and neither is a live test; `unknown` is not a claim that
  // anything is quiet, so it fails this chip too.
  if (filters.doingSomething && !isLiveState(state)) return false;
  if (filters.changedWithinDays !== null) {
    const since = daysSince(lastChangeOf(row)?.at, ctx.now);
    // Never changed reads as "not changed recently", which is the honest answer
    // to the filter's question.
    if (since === null || since > filters.changedWithinDays) return false;
  }
  return matchesRegisterSearch(row, filters.q);
}

export function filterRegister(
  rows: readonly AdminExperimentRecord[],
  filters: RegisterFilters,
  ctx: RegisterContext,
): AdminExperimentRecord[] {
  return rows.filter((row) => matchesRegisterFilters(row, filters, ctx));
}

// ── Sort ───────────────────────────────────────────────────────────────────

export const REGISTER_SORT_KEYS = [
  "key",
  "kind",
  "unit",
  "state",
  "allocation",
  "exposures",
  "cost",
  "errors",
  "p95",
  "days",
  "owner",
  "expires",
  "monday",
  "lastChange",
] as const;
export type RegisterSortKey = (typeof REGISTER_SORT_KEYS)[number];
export type RegisterSort = { key: RegisterSortKey; dir: "asc" | "desc" };

/** State then last change: the experiments somebody has to think about today,
 *  most recently touched first. */
export const DEFAULT_REGISTER_SORT: RegisterSort = { key: "state", dir: "asc" };

/** Sort order for the state column, needing-attention first. Not alphabetical:
 *  an alphabetical state column puts "canary" above "split" and "off" above
 *  everything, which is the opposite of useful. Live splits, then the verdict
 *  that is owed, then the settled states, then the ones we could not read. */
export const STATE_ORDER: readonly string[] = REGISTER_STATES;

function stateRank(state: string): number {
  const i = STATE_ORDER.indexOf(state);
  return i === -1 ? STATE_ORDER.length : i;
}

/** The baseline-excluded share of the population, from the record's own
 *  allocation. Shared with the detail page's headline. */
function allocationPct(row: AdminExperimentRecord): number {
  return dialledPct(row.kind, row.allocation);
}

/** One arm strip's value for a numeric column, averaged over the arms that have
 *  one. Null when no arm was measured, so the column sorts those out to the end
 *  rather than treating them as free and fast. */
function stripAverage(
  row: AdminExperimentRecord,
  read: (s: AdminArmStrip) => number | null | undefined,
): number | null {
  const values = row.strips.map(read).filter((v): v is number => v !== null && v !== undefined);
  if (values.length === 0) return null;
  return values.reduce((n, v) => n + v, 0) / values.length;
}

/** The comparable value of one cell. `null` means unknown, and unknown always
 *  sorts last whatever the direction, because flipping a column must not
 *  promote the rows we know nothing about to the top. */
export function registerSortValue(
  row: AdminExperimentRecord,
  key: RegisterSortKey,
): string | number | null {
  switch (key) {
    case "key":
      return row.key;
    case "kind":
      return row.kind;
    case "unit":
      return row.unit;
    case "state":
      return stateRank(stateOf(row));
    case "allocation":
      return allocationPct(row);
    case "exposures":
      return totalExposuresOf(row);
    case "cost":
      return stripAverage(row, (s) => s.costPerCallUsd);
    case "errors":
      return stripAverage(row, (s) => s.errorRate);
    case "p95":
      return stripAverage(row, (s) => s.p95LatencyMs);
    case "days":
      return daysRunningOf(row);
    case "owner":
      return row.owner || null;
    case "expires":
      // The ISO string sorts correctly as a string, so the lapsed ones group at the top
      // on an ascending sort without a second representation of the same date.
      return row.expires || null;
    case "monday":
      return row.monday || null;
    case "lastChange":
      return lastChangeOf(row)?.at ?? null;
  }
}

function compareValues(a: string | number | null, b: string | number | null): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1; // unknown last, both directions
  if (b === null) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b));
}

export function sortRegister(
  rows: readonly AdminExperimentRecord[],
  sort: RegisterSort,
): AdminExperimentRecord[] {
  const flip = sort.dir === "desc" ? -1 : 1;
  return [...rows].sort((x, y) => {
    const a = registerSortValue(x, sort.key);
    const b = registerSortValue(y, sort.key);
    // The unknown rule survives the direction flip: compare first, then flip
    // only when BOTH values are known.
    if (a === null || b === null) return compareValues(a, b);
    const primary = compareValues(a, b) * flip;
    if (primary !== 0) return primary;
    // The documented tiebreak: last change, newest first, then the key so the
    // order is stable across renders.
    const byChange = compareValues(lastChangeOf(y)?.at ?? null, lastChangeOf(x)?.at ?? null);
    return byChange !== 0 ? byChange : x.key.localeCompare(y.key);
  });
}

/** Clicking a header: the same column flips direction, a new column starts
 *  ascending. */
export function nextSort(current: RegisterSort, key: RegisterSortKey): RegisterSort {
  if (current.key !== key) return { key, dir: "asc" };
  return { key, dir: current.dir === "asc" ? "desc" : "asc" };
}

// ── Saved views ────────────────────────────────────────────────────────────

export const SAVED_VIEWS = [
  "attention",
  "running",
  "decision",
  "breached",
  "decided",
  "mine",
] as const;
export type SavedView = (typeof SAVED_VIEWS)[number];

export const SAVED_VIEW_LABELS: Record<SavedView, string> = {
  attention: "Needs attention",
  running: "Splits running",
  decision: "Needs a decision",
  breached: "Breached",
  decided: "Decided",
  mine: "Mine",
};

/** How long an experiment runs before the register says somebody owes it a
 *  verdict. Two weeks: long enough that a weekly cycle has seen it twice, short
 *  enough that a forgotten 50/50 split surfaces before it is a quarter old. */
export const NEEDS_DECISION_DAYS = 14;

/** How long a split may sit undecided before "needs attention" picks it up.
 *  Shorter than `NEEDS_DECISION_DAYS`: this view is the morning triage list, so
 *  it wants a split a week old on it while there is still time to act, and the
 *  decision view is the harder deadline. */
export const NEEDS_ATTENTION_DAYS = 7;

export function parseSavedView(raw: unknown): SavedView | undefined {
  return typeof raw === "string" && (SAVED_VIEWS as readonly string[]).includes(raw)
    ? (raw as SavedView)
    : undefined;
}

/** A saved view is just a filter set, so a reader can see exactly what it did
 *  and then edit it. Nothing is hidden behind the button. */
export function savedViewFilters(view: SavedView, me: string): RegisterFilters {
  const base = { ...DEFAULT_REGISTER_FILTERS };
  switch (view) {
    case "attention":
      // No field of its own: the row predicate below does the work, over the
      // whole register rather than a pre-narrowed slice, because a breached
      // experiment that is also `off` still wants looking at.
      return { ...base };
    case "running":
      return { ...base, doingSomething: true };
    case "decision":
      // No field of its own either: `needsDecision` below is the rule, and it
      // reaches a SHIPPED experiment that nobody ever filed a verdict on, which
      // a "splitting somebody only" filter would have hidden from the one view
      // whose whole job is to find the decisions still owed. The "past target
      // exposures" half of the plan's definition needs a declared target, which
      // no experiment carries yet.
      return { ...base };
    case "breached":
      return { ...base, hasBreaches: true };
    case "decided":
      return { ...base, state: "decided" };
    case "mine":
      return { ...base, owner: me };
  }
}

/** The one view that needs more than a filter set: "needs a decision" also
 *  wants the days, which is a per-row fact rather than a field. */
export function needsDecision(row: AdminExperimentRecord): boolean {
  const state = stateOf(row);
  if (!isLiveState(state) && state !== "shipped") return false;
  const days = daysRunningOf(row);
  return days !== null && days >= NEEDS_DECISION_DAYS;
}

/** "Needs attention": a guardrail is in breach, or the population has been split
 *  for more than a week with nobody having recorded a verdict.
 *
 *  Both halves are things that get worse by being left. A breach is a cost or an
 *  error rate somebody is paying for right now; a week-old split with no decision
 *  is an experiment that has stopped being an experiment and become a
 *  configuration nobody chose. */
export function needsAttention(row: AdminExperimentRecord, breached: ReadonlySet<string>): boolean {
  if (breached.has(row.key)) return true;
  if (registerFactsOf(row)?.guardrailDots.some((d) => d.status === "breach")) return true;
  if (!isLiveState(stateOf(row))) return false;
  if (decisionOf(row) !== null) return false;
  const days = daysRunningOf(row);
  return days !== null && days > NEEDS_ATTENTION_DAYS;
}

export function applySavedView(
  rows: readonly AdminExperimentRecord[],
  view: SavedView | undefined,
  ctx?: RegisterContext,
): AdminExperimentRecord[] {
  if (view === "decision") return rows.filter(needsDecision);
  if (view === "attention") {
    const breached = ctx?.breachedKeys ?? new Set<string>();
    return rows.filter((row) => needsAttention(row, breached));
  }
  return [...rows];
}

// ── Grouping ───────────────────────────────────────────────────────────────
//
// The register lists everything, so it needs a shape. Groups are by KIND, with
// one exception pinned above them: every row that is currently splitting the
// population, whatever its kind. "What is live right now" is still the first
// question, and the whole point of this page is that answering it must not mean
// hiding the other ninety-six rows.

/** The group a row belongs to. `splits` is the pinned one; every other row is
 *  grouped by its kind, so the id is the kind itself. */
export const SPLITS_GROUP = "splits";

export type RegisterGroup = {
  /** `splits`, or a registry kind. */
  id: string;
  label: string;
  rows: AdminExperimentRecord[];
  breakdown: { state: RegisterStateName; count: number }[];
};

const GROUP_LABELS: Record<string, string> = {
  [SPLITS_GROUP]: "Splits running",
  ui: "Product",
  section: "Prompt sections",
  prompt: "Prompt approaches",
  injection: "Injections",
  logic: "Logic",
  config: "Config",
};

export function groupLabel(id: string): string {
  return GROUP_LABELS[id] ?? id;
}

/** The order the kind groups appear in below the pinned splits. Declaration
 *  order rather than alphabetical, so the page reads the way the registry is
 *  built (`registry.records`) and a kind is always in the same place. */
/** How an experiment's declared expiry reads TODAY.
 *
 *  `lapsed` is a WARNING and nothing else. Nothing is gated, auto-killed or deleted when
 *  an expiry passes: every experiment is meant to exist for as long as somebody wants it
 *  to, and turning one off must stay instant, so the only friction an expiry represents was
 *  paid at DECLARATION time where it is a code review. What this makes visible is the set
 *  nobody has looked at since they were declared.
 *
 *  `unknown` is an honest answer, not a missing one: four of the six kinds carry no expiry
 *  yet, and rendering those as "fine" would claim a review that never happened.
 *
 *  Compared on the ISO date STRING rather than by parsing. A plain `YYYY-MM-DD` sorts
 *  lexicographically, the declarations are written that way, and `new Date("2026-12-31")`
 *  drags in the viewer's timezone -- which would make an experiment lapse a day early in
 *  Tel Aviv and a day late in California for the same declaration. */
export type ExpiryState = "unknown" | "live" | "lapsed";

export function expiryState(expires: string, today: string): ExpiryState {
  if (!expires) return "unknown";
  return expires < today ? "lapsed" : "live";
}

/** `YYYY-MM-DD` for the viewer's own day, which is the day they are asking about. */
export function todayIso(now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

export const KIND_GROUP_ORDER: readonly string[] = [
  "ui",
  "section",
  "prompt",
  "injection",
  "logic",
  "config",
];

/** Rows into groups: the splits first, as one group of their own, then one
 *  group per kind holding everything that is NOT already pinned above.
 *
 *  A split appears once, in the pinned group, not twice: a register that showed
 *  the same key in two places would make its own counts unreadable. The kind
 *  group's count and breakdown therefore describe what is actually in it. */
export function groupRegister(rows: readonly AdminExperimentRecord[]): RegisterGroup[] {
  const live = rows.filter((row) => isLiveState(stateOf(row)));
  const rest = rows.filter((row) => !isLiveState(stateOf(row)));
  const out: RegisterGroup[] = [];
  if (live.length > 0) {
    out.push({
      id: SPLITS_GROUP,
      label: groupLabel(SPLITS_GROUP),
      rows: live,
      breakdown: stateBreakdown(live),
    });
  }
  const kinds = [...new Set(rest.map((r) => r.kind))].sort((a, b) => {
    const ai = KIND_GROUP_ORDER.indexOf(a);
    const bi = KIND_GROUP_ORDER.indexOf(b);
    if (ai === -1 && bi === -1) return a.localeCompare(b);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });
  for (const kind of kinds) {
    const mine = rest.filter((r) => r.kind === kind);
    out.push({ id: kind, label: groupLabel(kind), rows: mine, breakdown: stateBreakdown(mine) });
  }
  return out;
}

// ── URL round trip ─────────────────────────────────────────────────────────
//
// A view is a link. Everything the filter bar holds goes in the query string,
// and only the NON-default values, so a shared URL is readable and a bare
// `/experiments` opens the default board.

export type RegisterSearch = {
  kind?: string;
  state?: string;
  agent?: string;
  unit?: string;
  owner?: string;
  layer?: string;
  q?: string;
  /** The "only what is splitting somebody" chip. Named for what it TURNS ON,
   *  because the default board is now the whole register: the parameter names
   *  the departure from the default, and the default moved. */
  doing?: boolean;
  breached?: boolean;
  monday?: boolean;
  changed?: number;
  sort?: string;
  dir?: "asc" | "desc";
  view?: SavedView;
};

export function registerSearchFrom(
  filters: RegisterFilters,
  sort: RegisterSort,
  view?: SavedView,
): RegisterSearch {
  const out: RegisterSearch = {};
  if (filters.kind) out.kind = filters.kind;
  if (filters.state) out.state = filters.state;
  if (filters.agent) out.agent = filters.agent;
  if (filters.unit) out.unit = filters.unit;
  if (filters.owner) out.owner = filters.owner;
  if (filters.layer) out.layer = filters.layer;
  if (filters.q) out.q = filters.q;
  if (filters.doingSomething) out.doing = true;
  if (filters.hasBreaches) out.breached = true;
  if (filters.hasMonday) out.monday = true;
  if (filters.changedWithinDays !== null) out.changed = filters.changedWithinDays;
  if (sort.key !== DEFAULT_REGISTER_SORT.key) out.sort = sort.key;
  if (sort.dir !== DEFAULT_REGISTER_SORT.dir) out.dir = sort.dir;
  if (view) out.view = view;
  return out;
}

function str(raw: unknown): string {
  return typeof raw === "string" ? raw : "";
}

function flag(raw: unknown): boolean {
  return raw === true || raw === "true" || raw === "1";
}

export function parseRegisterSearch(raw: Record<string, unknown>): {
  filters: RegisterFilters;
  sort: RegisterSort;
  view: SavedView | undefined;
} {
  const changedRaw = Number(raw.changed);
  const sortKey = str(raw.sort);
  const dir = raw.dir === "desc" ? "desc" : raw.dir === "asc" ? "asc" : undefined;
  return {
    filters: {
      kind: str(raw.kind),
      state: str(raw.state),
      agent: str(raw.agent),
      unit: str(raw.unit),
      owner: str(raw.owner),
      layer: str(raw.layer),
      q: str(raw.q),
      // `all=true` was the old spelling of "show me everything", and everything
      // is now the default: a bookmarked link carrying it still opens the whole
      // register, which is what its author asked for.
      doingSomething: flag(raw.doing),
      hasBreaches: flag(raw.breached),
      hasMonday: flag(raw.monday),
      changedWithinDays:
        Number.isFinite(changedRaw) && changedRaw > 0 ? Math.floor(changedRaw) : null,
    },
    sort: {
      key: (REGISTER_SORT_KEYS as readonly string[]).includes(sortKey)
        ? (sortKey as RegisterSortKey)
        : DEFAULT_REGISTER_SORT.key,
      dir: dir ?? DEFAULT_REGISTER_SORT.dir,
    },
    view: parseSavedView(raw.view),
  };
}

// ── CSV export ─────────────────────────────────────────────────────────────

export const REGISTER_CSV_HEADER = [
  "key",
  "label",
  "kind",
  "unit",
  "state",
  "allocation",
  "exposures",
  "cost_per_call_usd",
  "error_rate",
  "p95_ms",
  "guardrail_breaches",
  "days_running",
  "owner",
  "monday",
  "last_change_by",
  "last_change_at",
  "source",
  "tier",
  "layer",
  "description",
] as const;

/** RFC 4180 quoting. Re-exported rather than redefined: the console-wide table
 *  export (`@recruiter/shared`'s `table-csv.ts`) quotes cells by the same rule,
 *  and two copies of an escaping function is one copy too many. */
export { csvCell };

export function registerCsvRow(
  row: AdminExperimentRecord,
  ctx: RegisterContext,
): (string | number | null)[] {
  const facts = registerFactsOf(row);
  const breaches = facts?.guardrailDots.filter((d) => d.status === "breach") ?? [];
  return [
    row.key,
    row.label,
    row.kind,
    row.unit,
    stateOf(row),
    summariseAllocation(row),
    totalExposuresOf(row),
    stripAverage(row, (s) => s.costPerCallUsd),
    stripAverage(row, (s) => s.errorRate),
    stripAverage(row, (s) => s.p95LatencyMs),
    breaches.map((d) => `${d.arm}:${d.guardrail}`).join(" ") ||
      (ctx.breachedKeys.has(row.key) ? "yes" : ""),
    daysRunningOf(row),
    row.owner,
    row.monday,
    facts?.lastChange?.actorEmail ?? "",
    facts?.lastChange?.at ?? "",
    row.source,
    row.tier,
    row.layer,
    row.description,
  ];
}

/** The CURRENT view as CSV: what is on the screen, in the order it is on the
 *  screen. Exporting the whole register instead would hand somebody a file that
 *  does not match the board they were looking at. */
export function registerCsv(rows: readonly AdminExperimentRecord[], ctx: RegisterContext): string {
  const lines = [REGISTER_CSV_HEADER.map(csvCell).join(",")];
  for (const row of rows) lines.push(registerCsvRow(row, ctx).map(csvCell).join(","));
  return lines.join("\n");
}

/** A stable, dated filename so two exports never overwrite each other in a
 *  downloads folder. */
export function registerCsvFilename(now: Date): string {
  return `experiments-${now.toISOString().slice(0, 10)}.csv`;
}
